import { FC, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PasswordGate } from "@/components/elements/PasswordGate";
import { verifySharingApi, useSharingDetail } from "@/api/services/main/sharing";
import { getCredentialsKey } from "@/features/sharing/utils/session-credentials";
import { TSharingEntityType } from "@/api/services/main/sharing/dtos/sharing.dto";

/** Map entity type to password gate description */
const ENTITY_TYPE_DESCRIPTIONS: Record<TSharingEntityType, string> = {
  talent: "人材情報を確認するにはパスワードを入力してください。",
  job: "求人情報を確認するにはパスワードを入力してください。",
  case: "案件情報を確認するにはパスワードを入力してください。",
};

/** Fallback when entity type is unknown (API hasn't returned it yet or doesn't provide it) */
const DEFAULT_DESCRIPTION = "情報を確認するにはパスワードを入力してください。";

/** Public password gate — verifies shared link then redirects to detail page */
export const SharedPasswordPage: FC = () => {
  const { hash } = useParams<{ hash: string }>();
  const navigate = useNavigate();
  const { data: sharingDetail } = useSharingDetail(hash);

  const handleValidate = useCallback(
    async (password: string) => {
      if (!hash) return false;
      try {
        const result = await verifySharingApi({ shareUrl: hash, sharePassword: password });
        // Store credentials in sessionStorage so detail page can re-verify on reload
        sessionStorage.setItem(getCredentialsKey(hash), JSON.stringify({ shareUrl: hash, sharePassword: password }));
        // Store password in sessionStorage so detail page can re-verify for fresh data
        navigate(`/shared/${hash}/detail`, {
          state: {
            entityType: result.data.entityType,
            entityData: result.data.data,
            shareType: result.data.shareType,
            enumOptions: result.data.enumOptions,
            masterData: result.data.masterData,
          },
        });
        return true;
      } catch {
        return false;
      }
    },
    [hash, navigate],
  );

  return (
    <PasswordGate
      password=""
      onValidate={handleValidate}
      description={
        sharingDetail?.data.entityType
          ? ENTITY_TYPE_DESCRIPTIONS[sharingDetail.data.entityType]
          : DEFAULT_DESCRIPTION
      }
    >
      <></>
    </PasswordGate>
  );
};
